(() => {
  'use strict';
  const root = window.Shopify?.routes?.root || '/';
  const feedbackKey = 'beocca-cart-discount-feedback';
  let currentCart, loading;
  let pending = false;
  const forms = () => Array.from(document.querySelectorAll('[data-cart-discount]'));
  const normalized = code => String(code || '').trim().toLowerCase();
  const applicableCodes = cart => (cart.discount_codes || []).filter(d => d.applicable).map(d => String(d.code));
  const applicable = (cart, code) => applicableCodes(cart).some(value => normalized(value) === normalized(code));
  async function request(path, body) {
    const response = await fetch(root + path, {
      method: body ? 'POST' : 'GET',
      headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
      ...(body ? { body: JSON.stringify(body) } : { cache: 'no-store' })
    });
    if (!response.ok) throw new Error('Cart request failed');
    return response.json();
  }
  const writeCodes = codes => request('cart/update.js', { discount: codes.join(',') });
  function showMessage(form, key) {
    const message = form.querySelector('[data-discount-msg]');
    if (!message) return;
    message.textContent = key ? form.getAttribute('data-msg-' + key) || '' : '';
    message.hidden = !message.textContent;
  }
  function render(form, cart) {
    const list = form.querySelector('[data-discount-applied]');
    if (!list) return;
    const codes = applicableCodes(cart);
    list.replaceChildren();
    list.hidden = codes.length === 0;
    form.classList.toggle('cart-discount--has-code', codes.length > 0);
    for (const code of codes) {
      const chip = document.createElement('span');
      chip.className = 'cart-discount__tag';
      const label = document.createElement('span');
      label.textContent = code;
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'cart-discount__remove';
      remove.dataset.discountRemove = code;
      remove.setAttribute('aria-label', (form.dataset.msgRemove || '') + ': ' + code);
      const icon = document.createElement('span');
      icon.textContent = '×';
      icon.setAttribute('aria-hidden', 'true');
      remove.append(icon);
      remove.disabled = pending;
      chip.append(label, remove);
      list.append(chip);
    }
  }
  function syncCart(cart) {
    currentCart = cart;
    const store = window.theme?.cart?.store;
    if (store) store.setState({ products: window.theme.cart.normalize ? window.theme.cart.normalize(cart) : cart });
    forms().forEach(form => render(form, cart));
    document.dispatchEvent(new CustomEvent('cart:discount-updated', { detail: { cart } }));
  }
  function setBusy(value) {
    pending = value;
    window.theme.cartMutationPending = value;
    document.dispatchEvent(new CustomEvent('beocca:cart-busy', { detail: { busy: value } }));
    for (const form of forms()) {
      form.setAttribute('aria-busy', String(value));
      form.querySelectorAll('button,input').forEach(control => { control.disabled = value; });
      const label = form.querySelector('[data-discount-btn-label]');
      if (label) {
        label.dataset.original ||= label.textContent;
        label.textContent = value ? form.dataset.msgApplying : label.dataset.original;
      }
    }
  }
  function finish(form, cart, message) {
    if (cart) syncCart(cart);
    forms().forEach(other => showMessage(other, message));
    if (cart && form.dataset.discountReload === 'true') {
      try {
        if (message) sessionStorage.setItem(feedbackKey, message);
        else sessionStorage.removeItem(feedbackKey);
      } catch (_) {}
      window.location.reload();
    }
  }
  async function update(form, code, remove) {
    if (pending || window.theme.cartMutationPending) return;
    if (!code) {
      showMessage(form, 'empty');
      form.querySelector('[data-discount-input]')?.focus();
      return;
    }
    if (!remove && code.includes(',')) { showMessage(form, 'invalid'); return; }
    let previous;
    let changed = false;
    setBusy(true);
    forms().forEach(other => showMessage(other, ''));
    try {
      await window.theme.flushCartNote?.();
      const before = await request('cart.js');
      previous = applicableCodes(before);
      const wanted = remove ? previous.filter(existing => normalized(existing) !== normalized(code))
        : [...previous.filter(existing => normalized(existing) !== normalized(code)), code];
      changed = true;
      let cart = await writeCodes(wanted);
      if (!remove && (!applicable(cart, code) || previous.some(existing => !applicable(cart, existing)))) {
        cart = await writeCodes(previous);
        finish(form, cart, 'invalid');
      } else {
        const input = form.querySelector('[data-discount-input]');
        if (input) input.value = '';
        finish(form, cart, '');
      }
    } catch (_) {
      let cart;
      try { cart = changed ? await writeCodes(previous) : await request('cart.js'); }
      catch (_) { try { cart = await request('cart.js'); } catch (_) {} }
      finish(form, cart, 'error');
    } finally { setBusy(false); }
  }
  class CartDiscountForm extends HTMLElement {
    connectedCallback() {
      if (currentCart) render(this, currentCart);
      if (!loading) {
        loading = request('cart.js').then(cart => {
          currentCart = cart;
          forms().forEach(form => render(form, cart));
        }).catch(() => { loading = null; });
      }
      if (this.dataset.discountReload === 'true') {
        try {
          const message = sessionStorage.getItem(feedbackKey);
          if (message) showMessage(this, message);
          sessionStorage.removeItem(feedbackKey);
        } catch (_) {}
      }
    }
  }
  if (!customElements.get('cart-discount-form')) customElements.define('cart-discount-form', CartDiscountForm);
  document.addEventListener('click', event => {
    const button = event.target.closest?.('[data-discount-apply],[data-discount-remove]');
    const form = button?.closest('[data-cart-discount]');
    if (!form) return;
    event.preventDefault();
    const remove = button.hasAttribute('data-discount-remove');
    const code = remove ? button.dataset.discountRemove : form.querySelector('[data-discount-input]')?.value.trim();
    update(form, code, remove);
  });
  document.addEventListener('keydown', event => {
    if (event.key !== 'Enter' || !event.target.matches('[data-discount-input]')) return;
    event.preventDefault();
    const form = event.target.closest('[data-cart-discount]');
    if (form) update(form, event.target.value.trim(), false);
  });
})();
